import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';

import {
    isMobile,
    Products,
    LocationSelector,
    navigateWindowTo,
} from '@ratehub/base-ui';
import {
    CreditCardLayout,
    useApplyForCreditCardCallback,
    REWARD_TYPES,
    FOCUS_VARIANTS,
    USER_CATEGORIES,
    REWARD_SORT_OPTIONS,
    MAX_EXPANDED_PRODUCT_TABLE_ROWS,
} from '@ratehub/cc-common';

import getCreditCardFilteredAsyncContainerFilters from '../functions/getCreditCardFilteredAsyncContainerFilters';
import useTrackSponsoredProductsHeapEvent from '../hooks/useTrackSponsoredProductsHeapEvent';
import ProductList from './ProductList';


function ProductListCreditCards({
    title,

    ctaMessage,
    ctaLink,
    maxRowsCollapsed = 4,
    maxRowsExpanded = MAX_EXPANDED_PRODUCT_TABLE_ROWS,

    initialRewardType,
    initialFetchedCards,
    initialFilterValues,
    initialUserCategory,
    getFormValues,
    onFetchValue,
    initialProvinceCode,
}) {
    // used to track sponsored cards onload
    useTrackSponsoredProductsHeapEvent(initialFetchedCards, Products.CREDIT_CARDS);

    const applyForCreditCardCallback = useApplyForCreditCardCallback();

    const [ isOptimizedForMobile, setIsOptimizedForMobile ] = useState();
    // this useEffect() ensures server and browser are in sync for first render, else havoc ensues
    const cachedIsMobile = isMobile();
    useEffect(() => {
        setIsOptimizedForMobile(cachedIsMobile);
    }, [ cachedIsMobile ]);

    // TODO: remove workaround after LocationSelector fixed for FilteredAsyncContainer
    // see https://ratehub.atlassian.net/browse/CCDEP-3541
    const [ provinceCode, setProvinceCode ] = useState(initialProvinceCode);
    function updateLocation(values) {
        if (values?.province?.code) {
            setProvinceCode(values.province.code);
        }
    }

    function handleCCCallback(cardInfo) {
        applyForCreditCardCallback({ cardInfo, provinceCode });
    }


    const {
        primaryFilters,
        additionalFilters,
    } = getCreditCardFilteredAsyncContainerFilters({
        ...initialFilterValues,
        initialRewardType,
        initialUserCategory,
        isOptimizedForMobile,
    });

    // append LocationSelector to additionalFilters, unless length of primaryFilters is < MAX_FILTERS_BEFORE_DESKTOP_WRAPPING,
    // but not if it would be the only filter in the additional ones
    (primaryFilters.length < MAX_FILTERS_BEFORE_DESKTOP_WRAPPING && additionalFilters.length === 0
        ? primaryFilters
        : additionalFilters).push(
        (
            <LocationSelector
                name="location"
                key="location"
                initialProvince={provinceCode}
                label={(
                    <FormattedMessage
                        id="web-components.cc-product-list.location.label"
                        defaultMessage="Location"
                    />
                )}
                className="filter-question-compact"
                errorMessageStyle="minimal"
                animationType="none"
                onChange={updateLocation}
            />
        ),
    );


    return (
        <ProductList
            title={title}

            ctaMessage={ctaMessage}
            ctaOnClick={() => navigateWindowTo(ctaLink, { isNewTab: true })}

            maxRowsCollapsed={maxRowsCollapsed}
            maxRowsExpanded={maxRowsExpanded}
            noMatchingItemsMessage={(
                <FormattedMessage
                    id="web-components.cc-product-list.no-matches.label"
                    defaultMessage="No credit cards match the search filters."
                />
            )}

            initialFilterValues={initialFilterValues}
            primaryFilters={primaryFilters}
            additionalFilters={additionalFilters}
            initialFetchedProducts={initialFetchedCards}
            getFormValues={getFormValues}
            onFetchValue={onFetchValue}
        >
            {(product, formValues) => (
                <CreditCardLayout
                    cardInfo={product}
                    productType={formValues?.productType}
                    // TODO: remove after defensive coding for widgets are introduced
                    initialFilterValues={initialFilterValues}

                    onClick={() => handleCCCallback(product)}

                    hasEligibilityCTA={true}
                    hasCategoryPill={false}
                    hasBestUsedForPill={true}

                    ribbonMessage={product.ribbonMessage}

                    focusVariant={getFocusVariant(formValues)}
                />
            )}
        </ProductList>
    );
}

ProductListCreditCards.propTypes = {
    title: PropTypes.string,

    ctaMessage: PropTypes.string,
    ctaLink: PropTypes.string,
    maxRowsCollapsed: PropTypes.number,
    maxRowsExpanded: PropTypes.number,

    initialRewardType: PropTypes.oneOf(Object.values(REWARD_TYPES)),
    initialFetchedCards: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object, // mobx observable array
    ]).isRequired,
    initialFilterValues: PropTypes.any.isRequired,
    initialUserCategory: PropTypes.oneOf(Object.values(USER_CATEGORIES)),
    getFormValues: PropTypes.func.isRequired,
    onFetchValue: PropTypes.func.isRequired,
    initialProvinceCode: PropTypes.string,
};

// how many filters comfortably fit on first row of FilteredAsyncContainer without wrapping
const MAX_FILTERS_BEFORE_DESKTOP_WRAPPING = 4;


/**
 * Return the preferred variant for the credit card’s focus area, based on filter values in the form.
 * As of this writing, focusVariant only has the potential to affect the Rewards calculation dialogue.
 * Also, this is a preference, not a requirement, and is just another factor that may affect the layout of the card.
 * Some card productTypes, or other aspects of its display context, may ignore it.
 *
 * @param {Object} formValues
 * @return {string}
 */
function getFocusVariant(formValues) {
    return formValues?.sortBy === REWARD_SORT_OPTIONS.SUBSEQUENT_YEAR_VALUE
        ? FOCUS_VARIANTS.subsequentYearRewards
        : FOCUS_VARIANTS.firstYearRewards;
}


export default observer(ProductListCreditCards);
